import { makeStyles, Theme } from '@material-ui/core';
import React, { useCallback, useEffect, useRef } from 'react';

import {
  GetNotificationActivitySubscription,
  GetNotificationActivitySubscriptionVariables,
  GetNotificationsActivityQuery,
  useGetNotificationsActivityQuery,
} from '../../../../graphql/gen/types';
import { GET_NOTIFICATION_ACTIVITY_SUBSCRIPTION } from '../../../../graphql/subscriptions/GetNotificationActivity';
import { spacings } from '../../../../theme';
import { AnalyticsLocation, useLogAnalytics } from '../../shared/logAnalytics';
import { FooterLoader } from '../FooterLoader';
import { updateWithSubscriptionItem } from '../updateWithSubscriptionItem';
import { FetchMoreCallback, useInfiniteScroll } from '../useInfiniteScroll';
import { NotificationItem } from './NotificationItem';

export const NotificationsView = React.memo(
  (): React.ReactElement => {
    useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.NOTIFICATIONS } });
    const { data, loading: initialLoading, fetchMore, subscribeToMore } = useGetNotificationsActivityQuery();
    const classes = useStyles();
    const notificationsContainerRef = useRef<HTMLDivElement>(null);

    // Subscriptions
    useEffect(() => {
      const unsubscribe = subscribeToMore<
        GetNotificationActivitySubscription,
        GetNotificationActivitySubscriptionVariables
      >({
        document: GET_NOTIFICATION_ACTIVITY_SUBSCRIPTION,
        variables: {},
        updateQuery: (
          prev: GetNotificationsActivityQuery,
          { subscriptionData }: { subscriptionData: { data: GetNotificationActivitySubscription } }
        ) => {
          const newItem = subscriptionData.data.notificationActivity;

          return {
            ...prev,
            loggedIn: {
              ...prev.loggedIn,
              notificationsActivity: {
                ...prev.loggedIn?.notificationsActivity,
                items: updateWithSubscriptionItem({
                  newItem,
                  existingItems: prev.loggedIn?.notificationsActivity?.items || [],
                  newItemLocation: 'front',
                }),
              },
            },
          };
        },
      });

      return unsubscribe;
    }, [subscribeToMore]);

    // Infinite scroll
    const nextCursor = data?.loggedIn?.notificationsActivity?.nextCursor;
    const onFetchMore: FetchMoreCallback = useCallback(
      (isCancelled) =>
        fetchMore({
          variables: { cursor: nextCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (
              !fetchMoreResult?.loggedIn?.notificationsActivity ||
              !prev.loggedIn?.notificationsActivity ||
              isCancelled()
            ) {
              return prev;
            }

            return {
              ...prev,
              loggedIn: {
                ...prev.loggedIn,
                notificationsActivity: {
                  ...prev.loggedIn?.notificationsActivity,
                  items: [
                    ...prev.loggedIn.notificationsActivity.items,
                    ...fetchMoreResult.loggedIn.notificationsActivity.items,
                  ],
                  nextCursor: fetchMoreResult.loggedIn.notificationsActivity.nextCursor,
                },
              },
            };
          },
        }),
      [fetchMore, nextCursor]
    );

    const infiniteScrollLoading = useInfiniteScroll({
      containerRef: notificationsContainerRef,
      onFetchMore,
      canFetchMore: !initialLoading && nextCursor != null,
    });

    const loading = initialLoading || infiniteScrollLoading;

    return (
      <div className={classes.root} ref={notificationsContainerRef}>
        {data?.loggedIn?.notificationsActivity?.items
          ?.filter((n) => !n.deleted)
          .map((n) => (
            <NotificationItem key={n.id} notification={n} />
          ))}

        <FooterLoader isLoading={loading} />
      </div>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'auto',
    paddingTop: spacings.normal,
    paddingLeft: spacings.normal,
    paddingRight: spacings.normal,
  },
}));
